import { lazy, Suspense, useMemo } from 'react';
import type { LoginTemplateProps, LoginTemplateId, TemplateComponentProps } from '../../types/loginTemplate';
import { mapLoginConfig } from '../../types/loginTemplate';
import LoginPageSkeleton from './LoginPageSkeleton';

const ClassicTemplate = lazy(() => import('./templates/ClassicTemplate'));
const SplitHealthcareTemplate = lazy(() => import('./templates/SplitHealthcareTemplate'));
const InfoFirstTemplate = lazy(() => import('./templates/InfoFirstTemplate'));
const MinimalTemplate = lazy(() => import('./templates/MinimalTemplate'));

/** Loading fallback while template chunk loads */
function TemplateLoadingFallback() {
  return <LoginPageSkeleton />;
}

/** Template registry – maps IDs to lazy components */
const TEMPLATE_COMPONENTS: Record<LoginTemplateId, React.LazyExoticComponent<React.ComponentType<TemplateComponentProps>>> = {
  classic: ClassicTemplate,
  'split-healthcare': SplitHealthcareTemplate,
  'info-first': InfoFirstTemplate,
  minimal: MinimalTemplate,
};

interface LoginTemplateRendererProps extends LoginTemplateProps {
  templateId?: LoginTemplateId;
}

/**
 * Selects and renders the login page template based on configuration.
 * Falls back to ClassicTemplate if an unknown ID is provided.
 * 
 * This component is responsible for:
 * 1. Selecting the correct template based on templateId
 * 2. Calling mapLoginConfig to create the normalized mapped config
 * 3. Passing both raw and mapped config to the template
 */
export default function LoginTemplateRenderer({ templateId, ...props }: LoginTemplateRendererProps) {
  const resolvedId = templateId || props.config.templateId || 'classic';
  const TemplateComponent = TEMPLATE_COMPONENTS[resolvedId] || ClassicTemplate;

  // Create mapped config with pre-computed values
  // Memoize to avoid recalculation on every render
  const mappedConfig = useMemo(
    () => mapLoginConfig(props.config, props.themeSettings),
    [props.config, props.themeSettings]
  );

  return (
    <Suspense fallback={<TemplateLoadingFallback />}>
      <TemplateComponent {...props} mappedConfig={mappedConfig} />
    </Suspense>
  );
}
